import { useCallback, useEffect, useRef } from 'react';
import { UseClickAwayListenerOptions } from './use-click-away-listener-options';
import { ownerDocument } from '../../util/owner-doc';

export const useClickAwayListener = (
  onClickAway: (event: MouseEvent | TouchEvent) => any,
  options: UseClickAwayListenerOptions = {},
): React.RefObject<any> => {
  const ref = useRef<Node>(null);

  // Internal handlers to watch for clicks/touches in the document outside of
  // the ref node.
  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent): void => {
      // Ignore events that have been `event.preventDefault()` marked.
      if (event.defaultPrevented) {
        return;
      }

      const { current: node } = ref;

      // Ensure we have a current node via the ref.
      if (!node) {
        return;
      }

      // Get the owner document of the node we're watching.
      const doc = ownerDocument(node);

      // Test if the node we're watching contains the target node (the node that
      // was clicked or touched).
      if (
        doc.documentElement &&
        doc.documentElement.contains(event.target as Node | null) &&
        !node.contains(event.target as Node | null)
      ) {
        // If not, then call the callback.
        onClickAway(event);
      }
    },
    [onClickAway],
  );

  // Add our event listeners to watch for click-away's.
  useEffect((): (() => void) => {
    if (options.enabled) {
      window.addEventListener('mousedown', handleClickAway);
    }
    return (): void => {
      window.removeEventListener('mousedown', handleClickAway);
    };
  }, [handleClickAway, options.enabled]);

  return ref;
};
